import { useState, useEffect, useMemo } from "react";
import { listPortalCommunities } from "services/communities.service";
import { renderErrorNotifications } from "helpers/error.helpers";

/**
 * Fetches all vendor communities and manages the selected community state.
 * Use on any page that requires a community context (Memberships, Events, etc.).
 *
 * @param {string|null} initialCommunityId - Optional pre-selected community ID (e.g. from URL params)
 * @returns {{
 *   communities: Array,
 *   communityOptions: Array<{value: string, label: string}>,
 *   selectedCommunityId: string|null,
 *   setSelectedCommunityId: Function,
 *   selectedCommunity: object|null,
 *   loading: boolean,
 * }}
 */
export function useCommunitySelector({
  isRootUser = true,
  initialCommunityId = null,
}) {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(isRootUser ? true : false);
  const [selectedCommunityId, setSelectedCommunityId] =
    useState(initialCommunityId);

  useEffect(() => {
    async function load() {
      if (!isRootUser) return;
      setLoading(true);
      const { data, errors } = await listPortalCommunities({
        page_size: 100,
        is_active: true,
        verification_status: "VERIFIED",
      });
      setLoading(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        return;
      }
      const list = data?.data ?? [];
      setCommunities(list);
      // Auto-select if only one community and none pre-selected
      if (!initialCommunityId && list.length === 1) {
        setSelectedCommunityId(list[0].community_id);
      }
    }
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const communityOptions = useMemo(
    () =>
      communities.map((c) => ({
        value: c.community_id,
        label: c.display_name || c.name,
      })),
    [communities]
  );

  const selectedCommunity = useMemo(
    () =>
      communities.find((c) => c.community_id === selectedCommunityId) ?? null,
    [communities, selectedCommunityId]
  );

  return {
    communities,
    communityOptions,
    selectedCommunityId,
    setSelectedCommunityId,
    selectedCommunity,
    loading,
  };
}
